import { Vector3 } from '@babylonjs/core';

const configOffset = 0.32;

/** Anchor point attached to the bone;
 * x/z control the offset adjustments,
 * y value is always `configOffset`
 */
const point = (bone: string, x = 0, z = 0) => ({
	bone,
	offset: new Vector3(x, configOffset, z),
});

/** One porch edge runs from bone start → bone end (using default offset) */
const span = (startBone: string, endBone: string) => ({
	start: point(startBone),
	end: point(endBone),
});

/** Column scale: bottom = configOffset, top = 0 */
const scaleColumnOf = (bottomBone: string, topBone: string) => ({
	bottom: point(bottomBone),
	top: { bone: topBone, offset: new Vector3(0, 0, 0) },
});

const frontPorchConfig = () => ({
	scaleColumn: scaleColumnOf('b_p_r1', 'b_p_r2'),
	configs: {
		front: span('b_p_l1', 'b_p_r1'),
		left: span('b_f_l1', 'b_p_l1'),
		right: span('b_f_r1', 'b_p_r1'),
	},
});

const middlePorchConfig = () => ({
	scaleColumn: scaleColumnOf('b_p_l3', 'b_p_l4'),
	configs: {
		left: span('b_p_l1', 'b_p_l3'),
	},
});

// Side porch variants differ only in fine-tune offsets, so each shed type gets its own factory
const cottageSidePorchConfig = () => ({
	scaleColumn: scaleColumnOf('b_p_r1', 'b_p_r2'),
	configs: {
		front: span('b_p_r1', 'b_f_l1'),
		left: { start: point('b_p_l1', 0.02, 0.02), end: point('b_p_r1', 0.02) },
		back: { start: point('b_p_l1', 0, 0.02), end: point('b_b_l1', 0, 0.02) },
	},
});

const gableSidePorchConfig = () => ({
	scaleColumn: scaleColumnOf('b_p_r1', 'b_p_r2'),
	configs: {
		front: span('b_p_r1', 'b_f_l1'),
		left: { start: point('b_p_l1', 0.02, 0.01), end: point('b_p_r1', 0.02) },
		back: { start: point('b_p_l1', 0, 0.02), end: point('b_b_l1', 0, 0.02) },
	},
});

const barnSidePorchConfig = () => ({
	scaleColumn: scaleColumnOf('b_p_r1', 'b_p_r2'),
	configs: {
		front: span('b_p_r1', 'b_f_l1'),
		left: span('b_p_l1', 'b_p_r1'),
		back: { start: point('b_p_l1', 0, 0.02), end: point('b_b_l1', 0, 0.02) },
	},
});

const cornerPorchLeftConfig = () => ({
	scaleColumn: scaleColumnOf('b_p_l4', 'b_p_l3'),
	configs: {
		front: span('b_p_l4', 'b_f_l1'),
		left: span('b_p_l1', 'b_p_l4'),
	},
});

const cornerPorchRightConfig = () => ({
	scaleColumn: scaleColumnOf('b_p_l1', 'b_p_l2'),
	configs: {
		left: span('b_p_l4', 'b_p_l1'),
		back: span('b_p_l4', 'b_b_l1'),
	},
});

const deluxePorchConfig = () => ({
	scaleColumn: scaleColumnOf('b_p_r1', 'b_p_r2'),
	configs: {
		right: { start: point('b_f_r1', -0.01), end: point('b_p_r1', -0.01) },
		front: span('b_p_l1', 'b_p_r1'),
		left: span('b_f_l1', 'b_p_l1'),
	},
});

const deluxePorchFlippedConfig = () => ({
	scaleColumn: scaleColumnOf('b_p_r1', 'b_p_r2'),
	configs: {
		right: { start: point('b_f_r1', -0.02), end: point('b_p_r1', -0.02) },
		front: { start: point('b_p_l1', 0, -0.02), end: point('b_p_r1', 0, -0.02) },
		left: { start: point('b_f_l1', 0.02), end: point('b_p_l1', 0.02) },
	},
});

const lShapePorchConfig = () => ({
	scaleColumn: scaleColumnOf('b_p_r1', 'b_p_r2'),
	configs: {
		front: span('b_p_l1', 'b_p_r1'),
		left: span('b_f_l1', 'b_p_l1'),
		right: span('b_f_r1', 'b_p_r1'),
	},
});

const lShapePorchRightConfig = () => ({
	scaleColumn: scaleColumnOf('b_p_r1', 'b_p_r2'),
	configs: {
		front: span('b_p_l1', 'b_p_r1'),
		left: span('b_f_l1', 'b_p_l1'),
		right: span('b_f_r1', 'b_p_r1'),
	},
});

export const internalPorchConfigs = {
	// Front porch — 3 shed types share the same shape
	standard_cottage_front_porch: frontPorchConfig(),
	standard_barn_front_porch: frontPorchConfig(),
	structure_cardinal_cabin: frontPorchConfig(),

	// Middle porch
	structure_aframe_middle_porch: middlePorchConfig(),
	structure_barn_middle_porch: middlePorchConfig(),

	// Side porch — each variant has its own custom offsets
	standard_cottage_side_porch: cottageSidePorchConfig(),
	structure_gable_sideporch: gableSidePorchConfig(),
	structure_barn_side_porch: barnSidePorchConfig(),

	// Corner porch
	structure_aframe_cornerporch: cornerPorchLeftConfig(),
	structure_barn_corner_porch: cornerPorchLeftConfig(),
	structure_aframe_cornerporch_right: cornerPorchRightConfig(),
	structure_barn_corner_porch_right: cornerPorchRightConfig(),

	// Deluxe porch
	structure_aframe_deluxe_porch: deluxePorchConfig(),
	structure_barn_deluxe_porch: deluxePorchConfig(),
	structure_aframe_deluxe_porch_flipped: deluxePorchFlippedConfig(),
	structure_barn_deluxe_porch_flipped: deluxePorchFlippedConfig(),

	// L-shape porch
	structure_aframe_lshape_porch: lShapePorchConfig(),
	structure_barn_lshape_porch: lShapePorchConfig(),
	structure_aframe_lshape_porch_right: lShapePorchRightConfig(),
	structure_barn_lshape_porch_right: lShapePorchRightConfig(),
};

export const switchPorchScaleColumn = {
	bottom: { bone: 'b_f_l1', offset: new Vector3(0, 0, 0) },
	top: { bone: 'b_f_l2', offset: new Vector3(0, 0, 0) },
};

export const switchPorchConfig = {
	front: {
		left: {
			start: { bone: 'b_fp_l1', offset: new Vector3(0, 0, 0) },
			end: { bone: 'b_f_l1', offset: new Vector3(0, 0, 0) },
		},
		right: {
			start: { bone: 'b_fp_r1', offset: new Vector3(0, 0, 0) },
			end: { bone: 'b_f_r1', offset: new Vector3(0, 0, 0) },
		},
		front: {
			start: { bone: 'b_f_l1', offset: new Vector3(0, 0, 0) },
			end: { bone: 'b_f_r1', offset: new Vector3(0, 0, 0) },
		},
	},
	side: {
		left: {
			start: { bone: 'b_b_l1', offset: new Vector3(0, 0, 0) },
			end: { bone: 'b_f_l1', offset: new Vector3(0, 0, 0) },
		},
		front: {
			start: { bone: 'b_f_l1', offset: new Vector3(0, 0, 0) },
			end: { bone: 'b_sp_r1', offset: new Vector3(0, 0, 0) },
		},
		back: {
			start: { bone: 'b_b_l1', offset: new Vector3(0, 0, 0) },
			end: { bone: 'b_sp_l1', offset: new Vector3(0, 0, 0) },
		},
	},
	middle: {
		left: {
			start: { bone: 'b_p_b_l1', offset: new Vector3(0, 0, 0) },
			end: { bone: 'b_p_f_l1', offset: new Vector3(0, 0, 0) },
		},
	},
	corner: {
		left: {
			front: {
				start: { bone: 'b_f_l1', offset: new Vector3(0, 0, 0) },
				end: { bone: 'b_p_f_r1', offset: new Vector3(0, 0, 0) },
			},
			left: {
				start: { bone: 'b_p_b_l1', offset: new Vector3(0, 0, 0) },
				end: { bone: 'b_f_l1', offset: new Vector3(0, 0, 0) },
			},
		},
		right: {
			back: {
				start: { bone: 'b_b_l1', offset: new Vector3(0, 0, 0) },
				end: { bone: 'b_p_b_r1', offset: new Vector3(0, 0, 0) },
			},
			left: {
				start: { bone: 'b_b_l1', offset: new Vector3(0, 0, 0) },
				end: { bone: 'b_p_f_l1', offset: new Vector3(0, 0, 0) },
			},
		},
	},
	l_shape: {
		left: {
			front: {
				start: { bone: 'b_f_l1', offset: new Vector3(0, 0, 0) },
				end: { bone: 'b_f_r1', offset: new Vector3(0, 0, 0) },
			},
			left: {
				start: { bone: 'b_p_f_l1', offset: new Vector3(0, 0, 0) },
				end: { bone: 'b_f_l1', offset: new Vector3(0, 0, 0) },
			},
			right: {
				start: { bone: 'b_p_b_r1', offset: new Vector3(0, 0, 0) },
				end: { bone: 'b_f_r1', offset: new Vector3(0, 0, 0) },
			},
		},
		right: {
			front: {
				start: { bone: 'b_f_l1', offset: new Vector3(0, 0, 0) },
				end: { bone: 'b_f_r1', offset: new Vector3(0, 0, 0) },
			},
			left: {
				start: { bone: 'b_p_b_l1', offset: new Vector3(0, 0, 0) },
				end: { bone: 'b_f_l1', offset: new Vector3(0, 0, 0) },
			},
			right: {
				start: { bone: 'b_p_f_r1', offset: new Vector3(0, 0, 0) },
				end: { bone: 'b_f_r1', offset: new Vector3(0, 0, 0) },
			},
		},
	},
};

export const sidesPorchConfig = {
	front: {
		front: ['front', 'porch'],
	},
	middle: {
		left: [
			'left_front',
			'left_back',
			'left_inner_porch',
			'right_inner_porch',
			'front_inner_porch',
		],
	},
	corner: {
		left: {
			left: ['left', 'inner_left'],
			front: ['front', 'inner_front'],
		},
		right: {
			left: ['left', 'inner_left'],
			back: ['back', 'inner_back'],
		},
	},
	l_shape: {
		left: {
			right: ['right', 'inner_right'],
			front: ['front', 'inner_front', 'porch'],
		},
		right: {
			left: ['left', 'inner_left'],
			front: ['front', 'inner_front', 'porch'],
		},
	},
};

export const skeletonMapping = {
	b_fp_l1: 'b_f_l1',
	b_fp_l2: 'b_f_l2',
	b_fp_r1: 'b_f_r1',
	b_fp_r2: 'b_f_r2',
	b_sp_l1: 'b_b_l1',
	b_sp_l2: 'b_b_l2',
	b_sp_r1: 'b_b_r1',
	b_sp_r2: 'b_b_r2',
	b_p_b_l1: 'b_p_b_l1',
	b_p_b_l2: 'b_p_b_l2',
	b_p_b_r1: 'b_p_b_r1',
	b_p_b_r2: 'b_p_b_r2',
	b_p_f_l1: 'b_p_f_l1',
	b_p_f_l2: 'b_p_f_l2',
	b_p_f_r1: 'b_p_f_r1',
	b_p_f_r2: 'b_p_f_r2',
};

// Draw Wall Data
export const dataDrawWallFrontPorch = {
	gambrel: [
		{
			side: 'porch',
			name: 'porch',
			oldName: '',
			position: 'b_f_l2',
			reverse: true,
			bones: [
				{
					name: 'B_F_L2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_F_L3',
					x: 0,
					y: 0,
				},
				{
					name: 'B_F_T',
					x: 0,
					y: 0,
				},
				{
					name: 'B_F_R3',
					x: 0,
					y: 0,
				},
				{
					name: 'B_F_R2',
					x: 0,
					y: 0,
				},
			],
		},
		{
			side: 'front',
			name: 'front_wall',
			oldName: '',
			position: 'b_fp_l1',
			bones: [
				{
					name: 'B_FP_R1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_FP_R2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_FP_L2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_FP_L1',
					x: 0,
					y: 0,
				},
			],
		},
		{
			side: 'left',
			name: 'left_wall',
			oldName: '',
			position: 'b_fp_l1',
			bones: [
				{
					name: 'B_FP_L1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_FP_L2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_B_L2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_B_L1',
					x: 0,
					y: 0,
				},
			],
		},
		{
			side: 'right',
			name: 'right_wall',
			oldName: '',
			position: 'b_fp_r1',
			bones: [
				{
					name: 'B_B_R1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_B_R2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_FP_R2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_FP_R1',
					x: 0,
					y: 0,
				},
			],
		},
		{
			side: 'floor',
			name: 'floor',
			oldName: '',
			position: 'b_b_l1',
			disableDrawInterior: 1,
			bones: [
				{
					name: 'B_FP_R1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_FP_L1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_B_L1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_B_R1',
					x: 0,
					y: 0,
				},
			],
		},
	],
	aframe: [
		{
			side: 'porch',
			name: 'porch',
			oldName: '',
			position: 'b_f_l1',
			bones: [
				{
					name: 'B_F_L2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_F_R2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_F_T',
					x: 0,
					y: 0,
				},
			],
		},
		{
			side: 'front',
			name: 'front_wall',
			oldName: '',
			position: 'b_fp_l1',
			bones: [
				{
					name: 'B_FP_R1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_FP_R2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_FP_L2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_FP_L1',
					x: 0,
					y: 0,
				},
			],
		},
		{
			side: 'left',
			name: 'left_wall',
			oldName: '',
			position: 'b_fp_l1',
			bones: [
				{
					name: 'B_FP_L1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_FP_L2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_B_L2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_B_L1',
					x: 0,
					y: 0,
				},
			],
		},
		{
			side: 'right',
			name: 'right_wall',
			oldName: '',
			position: 'b_fp_r1',
			bones: [
				{
					name: 'B_B_R1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_B_R2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_FP_R2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_FP_R1',
					x: 0,
					y: 0,
				},
			],
		},
		{
			side: 'floor',
			name: 'floor',
			oldName: '',
			position: 'b_b_l1',
			disableDrawInterior: 1,
			bones: [
				{
					name: 'B_FP_R1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_FP_L1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_B_L1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_B_R1',
					x: 0,
					y: 0,
				},
			],
		},
	],
};

export const dataDrawWallSidePorch = {
	gambrel: [
		{
			side: 'floor',
			name: 'floor',
			oldName: '',
			position: 'b_f_l1',
			disableDrawInterior: 1,
			bones: [
				{
					name: 'b_sp_l1',
					x: 0,
					y: 0,
				},
				{
					name: 'b_sp_r1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_F_R1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_B_R1',
					x: 0,
					y: 0,
				},
			],
		},
		{
			side: 'back',
			name: 'back_wall',
			oldName: '',
			position: 'b_sp_l1',
			reverse: 0,
			bones: [
				{
					name: 'b_sp_l1',
					x: 0,
					y: 0,
				},
				{
					name: 'b_sp_l2',
					x: 0,
					y: 0,
				},
				{
					name: 'b_b_l2',
					x: 0,
					y: 0,
				},
				{
					name: 'b_b_l3',
					x: 0,
					y: 0,
				},
				{
					name: 'b_b_t',
					x: 0,
					y: 0,
				},
				{
					name: 'b_b_r3',
					x: 0,
					y: 0,
				},
				{
					name: 'b_b_r2',
					x: 0,
					y: 0,
				},
				{
					name: 'b_b_r1',
					x: 0,
					y: 0,
				},
			],
		},
		{
			side: 'front',
			name: 'front_wall',
			oldName: '',
			position: 'b_sp_r1',
			reverse: 1,
			bones: [
				{
					name: 'b_sp_r1',
					x: 0,
					y: 0,
				},
				{
					name: 'b_sp_r2',
					x: 0,
					y: 0,
				},
				{
					name: 'b_f_l2',
					x: 0,
					y: 0,
				},
				{
					name: 'b_f_l3',
					x: 0,
					y: 0,
				},
				{
					name: 'b_f_t',
					x: 0,
					y: 0,
				},
				{
					name: 'b_f_r3',
					x: 0,
					y: 0,
				},
				{
					name: 'b_f_r2',
					x: 0,
					y: 0,
				},
				{
					name: 'b_f_r1',
					x: 0,
					y: 0,
				},
			],
		},
		{
			side: 'left',
			name: 'left_wall',
			oldName: '',
			position: 'b_sp_l1',
			reverse: 1,
			bones: [
				{
					name: 'b_sp_l1',
					x: 0,
					y: 0,
				},
				{
					name: 'b_sp_l2',
					x: 0,
					y: 0,
				},
				{
					name: 'b_sp_r2',
					x: 0,
					y: 0,
				},
				{
					name: 'b_sp_r1',
					x: 0,
					y: 0,
				},
			],
		},
	],
	aframe: [
		{
			side: 'floor',
			name: 'floor',
			oldName: '',
			position: 'b_f_r1',
			disableDrawInterior: 1,
			bones: [
				{
					name: 'b_sp_l1',
					x: 0,
					y: 0,
				},
				{
					name: 'b_sp_r1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_F_R1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_B_R1',
					x: 0,
					y: 0,
				},
			],
		},
		{
			side: 'back',
			name: 'back_wall',
			oldName: '',
			position: 'b_sp_l1',
			reverse: 0,
			bones: [
				{
					name: 'b_sp_l1',
					x: 0,
					y: 0,
				},
				{
					name: 'b_sp_l2',
					x: 0,
					y: 0,
				},
				{
					name: 'b_b_l2',
					x: 0,
					y: 0,
				},
				{
					name: 'b_b_t',
					x: 0,
					y: 0,
				},
				{
					name: 'b_b_r2',
					x: 0,
					y: 0,
				},
				{
					name: 'b_b_r1',
					x: 0,
					y: 0,
				},
			],
		},
		{
			side: 'front',
			name: 'front_wall',
			oldName: '',
			position: 'b_sp_r1',
			reverse: 1,
			bones: [
				{
					name: 'b_sp_r1',
					x: 0,
					y: 0,
				},
				{
					name: 'b_sp_r2',
					x: 0,
					y: 0,
				},
				{
					name: 'b_f_l2',
					x: 0,
					y: 0,
				},
				{
					name: 'b_f_t',
					x: 0,
					y: 0,
				},
				{
					name: 'b_f_r2',
					x: 0,
					y: 0,
				},
				{
					name: 'b_f_r1',
					x: 0,
					y: 0,
				},
			],
		},
		{
			side: 'left',
			name: 'left_wall',
			oldName: '',
			position: 'b_sp_l1',
			reverse: 1,
			bones: [
				{
					name: 'b_sp_l1',
					x: 0,
					y: 0,
				},
				{
					name: 'b_sp_l2',
					x: 0,
					y: 0,
				},
				{
					name: 'b_sp_r2',
					x: 0,
					y: 0,
				},
				{
					name: 'b_sp_r1',
					x: 0,
					y: 0,
				},
			],
		},
	],
};

export const dataDrawWallMiddlePorch = {
	aframe: [
		{
			side: 'floor',
			name: 'floor',
			oldName: '',
			position: 'b_f_l1',
			disableDrawInterior: 1,
			bones: [
				{
					name: 'B_F_L1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_F_R1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_B_R1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_B_L1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_P_B_L1',
					x: 0,
					y: -0.02,
				},
				{
					name: 'B_P_B_R1',
					x: 0,
					y: -0.02,
				},
				{
					name: 'B_P_F_R1',
					x: 0,
					y: 0.02,
				},
				{
					name: 'B_P_F_L1',
					x: 0,
					y: 0.02,
				},
			],
		},
		{
			side: 'left_back',
			name: 'left_back_wall',
			oldName: '',
			position: 'b_b_l1',
			disableDrawInterior: 0,
			reverse: 1,
			bones: [
				{
					name: 'B_B_L1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_B_L2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_P_B_L2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_P_B_L1',
					x: 0,
					y: 0,
				},
			],
		},
		{
			side: 'left_front',
			name: 'left_front_wall',
			oldName: '',
			position: 'b_f_l1',
			disableDrawInterior: 0,
			reverse: 0,
			bones: [
				{
					name: 'B_F_L1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_F_L2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_P_F_L2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_P_F_L1',
					x: 0,
					y: 0,
				},
			],
		},
		{
			side: 'left_inner_porch',
			name: 'left_inner_porch_wall',
			oldName: '',
			position: 'b_p_f_l1',
			disableDrawInterior: 0,
			reverse: 0,
			bones: [
				{
					name: 'B_P_F_L1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_P_F_L2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_P_F_R2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_P_F_R1',
					x: 0,
					y: 0,
				},
			],
		},
		{
			side: 'right_inner_porch',
			name: 'right_inner_porch_wall',
			oldName: '',
			position: 'b_p_b_l1',
			disableDrawInterior: 0,
			reverse: 1,
			bones: [
				{
					name: 'B_P_B_L1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_P_B_L2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_P_B_R2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_P_B_R1',
					x: 0,
					y: 0,
				},
			],
		},
		{
			side: 'front_inner_porch',
			name: 'front_inner_porch_wall',
			oldName: '',
			position: 'b_p_f_r1',
			disableDrawInterior: 0,
			reverse: 0,
			bones: [
				{
					name: 'B_P_F_R1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_P_F_R2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_P_B_R2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_P_B_R1',
					x: 0,
					y: 0,
				},
			],
		},
	],
	gambrel: [
		{
			side: 'floor',
			name: 'floor',
			oldName: '',
			position: 'b_f_l1',
			disableDrawInterior: 1,
			bones: [
				{
					name: 'B_F_L1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_F_R1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_B_R1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_B_L1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_P_B_L1',
					x: 0,
					y: -0.02,
				},
				{
					name: 'B_P_B_R1',
					x: 0,
					y: -0.02,
				},
				{
					name: 'B_P_F_R1',
					x: 0,
					y: 0.02,
				},
				{
					name: 'B_P_F_L1',
					x: 0,
					y: 0.02,
				},
			],
		},
		{
			side: 'left_back',
			name: 'left_back_wall',
			oldName: '',
			position: 'b_b_l1',
			disableDrawInterior: 0,
			reverse: 1,
			bones: [
				{
					name: 'B_B_L1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_B_L2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_P_B_L2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_P_B_L1',
					x: 0,
					y: 0,
				},
			],
		},
		{
			side: 'left_front',
			name: 'left_front_wall',
			oldName: '',
			position: 'b_f_l1',
			disableDrawInterior: 0,
			reverse: 0,
			bones: [
				{
					name: 'B_F_L1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_F_L2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_P_F_L2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_P_F_L1',
					x: 0,
					y: 0,
				},
			],
		},
		{
			side: 'left_inner_porch',
			name: 'left_inner_porch_wall',
			oldName: '',
			position: 'b_p_f_l1',
			disableDrawInterior: 0,
			reverse: 0,
			bones: [
				{
					name: 'B_P_F_L1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_P_F_L2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_P_F_R2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_P_F_R1',
					x: 0,
					y: 0,
				},
			],
		},
		{
			side: 'right_inner_porch',
			name: 'right_inner_porch_wall',
			oldName: '',
			position: 'b_p_b_l1',
			disableDrawInterior: 0,
			reverse: 1,
			bones: [
				{
					name: 'B_P_B_L1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_P_B_L2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_P_B_R2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_P_B_R1',
					x: 0,
					y: 0,
				},
			],
		},
		{
			side: 'front_inner_porch',
			name: 'front_inner_porch_wall',
			oldName: '',
			position: 'b_p_f_r1',
			disableDrawInterior: 0,
			reverse: 0,
			bones: [
				{
					name: 'B_P_F_R1',
					x: 0,
					y: 0,
				},
				{
					name: 'B_P_F_R2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_P_B_R2',
					x: 0,
					y: 0,
				},
				{
					name: 'B_P_B_R1',
					x: 0,
					y: 0,
				},
			],
		},
	],
};

export const dataDrawWallCornerPorch = {
	left: {
		gambrel: [
			{
				side: 'front',
				name: 'front_wall',
				oldName: '',
				position: 'b_f_r1',
				bones: [
					{
						name: 'B_F_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_F_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_F_R3',
						x: 0,
						y: 0,
					},
					{
						name: 'B_F_T',
						x: 0,
						y: 0,
					},
					{
						name: 'B_F_L3',
						x: 0,
						y: 0,
					},
					{
						name: 'B_F_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'left',
				name: 'left_wall',
				oldName: '',
				position: 'b_b_l1',
				reverse: true,
				bones: [
					{
						name: 'B_B_L1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_L1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'inner_left',
				name: 'inner_left_wall',
				oldName: '',
				position: 'b_p_b_r1',
				reverse: true,
				bones: [
					{
						name: 'B_P_B_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'inner_front',
				name: 'inner_front_wall',
				oldName: '',
				position: 'b_p_b_r1',
				bones: [
					{
						name: 'B_P_B_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_L1',
						x: 0,
						y: 0,
					},
				],
			},
		],
		aframe: [
			{
				side: 'front',
				name: 'front_wall',
				oldName: '',
				position: 'b_f_r1',
				bones: [
					{
						name: 'B_F_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_F_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_F_T',
						x: 0,
						y: 0,
					},
					{
						name: 'B_F_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'left',
				name: 'left_wall',
				oldName: '',
				position: 'b_b_l1',
				reverse: true,
				bones: [
					{
						name: 'B_B_L1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_L1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'inner_left',
				name: 'inner_left_wall',
				oldName: '',
				position: 'b_p_b_r1',
				reverse: true,
				bones: [
					{
						name: 'B_P_B_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'inner_front',
				name: 'inner_front_wall',
				oldName: '',
				position: 'b_p_b_r1',
				bones: [
					{
						name: 'B_P_B_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_L1',
						x: 0,
						y: 0,
					},
				],
			},
		],
	},
	right: {
		gambrel: [
			{
				side: 'back',
				name: 'back_wall',
				oldName: '',
				position: 'b_b_r1',
				reverse: true,
				bones: [
					{
						name: 'B_B_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_R3',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_T',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_L3',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_R1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'left',
				name: 'left_wall',
				oldName: '',
				position: 'b_f_l1',
				bones: [
					{
						name: 'B_F_L1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_F_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_L1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'inner_left',
				name: 'inner_left_wall',
				oldName: '',
				position: 'b_p_f_r1',
				bones: [
					{
						name: 'B_P_F_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_R1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'inner_back',
				name: 'inner_back_wall',
				oldName: '',
				position: 'b_p_f_r1',
				reverse: true,
				bones: [
					{
						name: 'B_P_F_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_L1',
						x: 0,
						y: 0,
					},
				],
			},
		],
		aframe: [
			{
				side: 'back',
				name: 'back_wall',
				oldName: '',
				position: 'b_b_r1',
				reverse: true,
				bones: [
					{
						name: 'B_B_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_T',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_R1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'left',
				name: 'left_wall',
				oldName: '',
				position: 'b_f_l1',
				bones: [
					{
						name: 'B_F_L1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_F_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_L1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'inner_left',
				name: 'inner_left_wall',
				oldName: '',
				position: 'b_p_f_r1',
				bones: [
					{
						name: 'B_P_F_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_R1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'inner_back',
				name: 'inner_back_wall',
				oldName: '',
				position: 'b_p_f_r1',
				reverse: true,
				bones: [
					{
						name: 'B_P_F_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_L1',
						x: 0,
						y: 0,
					},
				],
			},
		],
	},
};

export const dataDrawWallLShapePorch = {
	left: {
		gambrel: [
			{
				side: 'floor',
				name: 'floor',
				oldName: '',
				position: 'b_b_l1',
				disableDrawInterior: 1,
				bones: [
					{
						name: 'B_B_L1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_L1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_L1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'front',
				name: 'front_wall',
				oldName: '',
				position: 'b_p_f_l1',
				reverse: true,
				bones: [
					{
						name: 'B_P_F_L1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'left',
				name: 'left_wall',
				oldName: '',
				position: 'b_p_f_l1',
				bones: [
					{
						name: 'B_P_F_L1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_L1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'inner_right',
				name: 'inner_right_wall',
				oldName: '',
				position: 'b_p_b_l1',
				bones: [
					{
						name: 'B_P_B_L1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'right',
				name: 'right_wall',
				oldName: '',
				position: 'b_b_r1',
				reverse: true,
				bones: [
					{
						name: 'B_P_B_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_R1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'inner_front',
				name: 'inner_front_wall',
				oldName: '',
				position: 'b_p_b_r1',
				bones: [
					{
						name: 'B_P_B_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_L1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'porch',
				name: 'porch_wall',
				oldName: '',
				position: 'b_f_r2',
				bones: [
					{
						name: 'B_F_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_F_R3',
						x: 0,
						y: 0,
					},
					{
						name: 'B_F_T',
						x: 0,
						y: 0,
					},
					{
						name: 'B_F_L3',
						x: 0,
						y: 0,
					},
					{
						name: 'B_F_L2',
						x: 0,
						y: 0,
					},
				],
			},
		],
		aframe: [
			{
				side: 'floor',
				name: 'floor',
				oldName: '',
				position: 'b_b_l1',
				disableDrawInterior: 1,
				bones: [
					{
						name: 'B_B_L1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_L1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_L1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'front',
				name: 'front_wall',
				oldName: '',
				position: 'b_p_f_l1',
				reverse: true,
				bones: [
					{
						name: 'B_P_F_L1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'left',
				name: 'left_wall',
				oldName: '',
				position: 'b_p_f_l1',
				bones: [
					{
						name: 'B_P_F_L1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_L1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'inner_right',
				name: 'inner_right_wall',
				oldName: '',
				position: 'b_p_b_l1',
				bones: [
					{
						name: 'B_P_B_L1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'right',
				name: 'right_wall',
				oldName: '',
				position: 'b_b_r1',
				reverse: true,
				bones: [
					{
						name: 'B_P_B_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_R1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'inner_front',
				name: 'inner_front_wall',
				oldName: '',
				position: 'b_p_b_r1',
				bones: [
					{
						name: 'B_P_B_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_L1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'porch',
				name: 'porch_wall',
				oldName: '',
				position: 'b_f_r2',
				bones: [
					{
						name: 'B_F_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_F_T',
						x: 0,
						y: 0,
					},
					{
						name: 'B_F_L2',
						x: 0,
						y: 0,
					},
				],
			},
		],
	},
	right: {
		gambrel: [
			{
				side: 'floor',
				name: 'floor',
				oldName: '',
				position: 'b_b_l1',
				disableDrawInterior: 1,
				bones: [
					{
						name: 'B_B_L1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_L1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_L1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'front',
				name: 'front_wall',
				oldName: '',
				position: 'b_p_f_l1',
				reverse: true,
				bones: [
					{
						name: 'B_P_F_L1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'left',
				name: 'left_wall',
				oldName: '',
				position: 'b_p_b_l1',
				bones: [
					{
						name: 'B_P_B_L1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_L1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'inner_left',
				name: 'inner_left_wall',
				oldName: '',
				position: 'b_p_b_r1',
				reverse: true,
				bones: [
					{
						name: 'B_P_B_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_L1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'right',
				name: 'right_wall',
				oldName: '',
				position: 'b_b_r1',
				reverse: true,
				bones: [
					{
						name: 'B_P_F_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_R1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'inner_front',
				name: 'inner_front_wall',
				oldName: '',
				position: 'b_p_b_r1',
				bones: [
					{
						name: 'B_P_B_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_L1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'porch',
				name: 'porch_wall',
				oldName: '',
				position: 'b_f_r2',
				bones: [
					{
						name: 'B_F_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_F_R3',
						x: 0,
						y: 0,
					},
					{
						name: 'B_F_T',
						x: 0,
						y: 0,
					},
					{
						name: 'B_F_L3',
						x: 0,
						y: 0,
					},
					{
						name: 'B_F_L2',
						x: 0,
						y: 0,
					},
				],
			},
		],
		aframe: [
			{
				side: 'floor',
				name: 'floor',
				oldName: '',
				position: 'b_b_l1',
				disableDrawInterior: 1,
				bones: [
					{
						name: 'B_B_L1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_L1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_L1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'front',
				name: 'front_wall',
				oldName: '',
				position: 'b_p_f_l1',
				reverse: true,
				bones: [
					{
						name: 'B_P_F_L1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'left',
				name: 'left_wall',
				oldName: '',
				position: 'b_p_b_l1',
				bones: [
					{
						name: 'B_P_B_L1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_L1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'inner_left',
				name: 'inner_left_wall',
				oldName: '',
				position: 'b_p_b_r1',
				reverse: true,
				bones: [
					{
						name: 'B_P_B_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_L1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'right',
				name: 'right_wall',
				oldName: '',
				position: 'b_b_r1',
				reverse: true,
				bones: [
					{
						name: 'B_P_F_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_F_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_B_R1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'inner_front',
				name: 'inner_front_wall',
				oldName: '',
				position: 'b_p_b_r1',
				bones: [
					{
						name: 'B_P_B_R1',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_L2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_P_B_L1',
						x: 0,
						y: 0,
					},
				],
			},
			{
				side: 'porch',
				name: 'porch_wall',
				oldName: '',
				position: 'b_f_r2',
				bones: [
					{
						name: 'B_F_R2',
						x: 0,
						y: 0,
					},
					{
						name: 'B_F_T',
						x: 0,
						y: 0,
					},
					{
						name: 'B_F_L2',
						x: 0,
						y: 0,
					},
				],
			},
		],
	},
};

// Corner trim data
export const dataCornerTrimFrontPorch = [
	{
		bone_start: {
			name: 'B_B_L1',
			x: null,
			y: null,
			z: null,
		},
		bone_end: {
			name: 'B_B_L2',
			x: null,
			y: null,
			z: null,
		},
		modelVersions: [],
		side_rotate: 'back-left',
	},
	{
		bone_start: {
			name: 'B_B_R1',
			x: null,
			y: null,
			z: null,
		},
		bone_end: {
			name: 'B_B_R2',
			x: null,
			y: null,
			z: null,
		},
		modelVersions: [],
		side_rotate: 'back-right',
	},
	{
		bone_start: {
			name: 'B_FP_L1',
			x: null,
			y: null,
			z: null,
		},
		bone_end: {
			name: 'B_FP_L2',
			x: null,
			y: null,
			z: null,
		},
		modelVersions: [],
		side_rotate: 'front-left',
	},
	{
		bone_start: {
			name: 'B_FP_R1',
			x: null,
			y: null,
			z: null,
		},
		bone_end: {
			name: 'B_FP_R2',
			x: null,
			y: null,
			z: null,
		},
		modelVersions: [],
		side_rotate: 'front-right',
	},
];

export const dataCornerTrimSidePorch = [
	{
		bone_start: {
			name: 'B_SP_L1',
			x: null,
			y: null,
			z: null,
		},
		bone_end: {
			name: 'B_SP_L2',
			x: null,
			y: null,
			z: null,
		},
		modelVersions: [],
		side_rotate: 'back-left',
	},
	{
		bone_start: {
			name: 'B_B_R1',
			x: null,
			y: null,
			z: null,
		},
		bone_end: {
			name: 'B_B_R2',
			x: null,
			y: null,
			z: null,
		},
		modelVersions: [],
		side_rotate: 'back-right',
	},
	{
		bone_start: {
			name: 'B_SP_R1',
			x: null,
			y: null,
			z: null,
		},
		bone_end: {
			name: 'B_SP_R2',
			x: null,
			y: null,
			z: null,
		},
		modelVersions: [],
		side_rotate: 'front-left',
	},
	{
		bone_start: {
			name: 'B_F_R1',
			x: null,
			y: null,
			z: null,
		},
		bone_end: {
			name: 'B_F_R2',
			x: null,
			y: null,
			z: null,
		},
		modelVersions: [],
		side_rotate: 'front-right',
	},
];

export const dataCornerTrimMiddlePorch = [
	{
		bone_start: {
			name: 'B_B_L1',
			x: null,
			y: null,
			z: null,
		},
		bone_end: {
			name: 'B_B_L2',
			x: null,
			y: null,
			z: null,
		},
		modelVersions: [],
		side_rotate: 'back-left',
	},
	{
		bone_start: {
			name: 'B_B_R1',
			x: null,
			y: null,
			z: null,
		},
		bone_end: {
			name: 'B_B_R2',
			x: null,
			y: null,
			z: null,
		},
		modelVersions: [],
		side_rotate: 'back-right',
	},
	{
		bone_start: {
			name: 'B_F_L1',
			x: null,
			y: null,
			z: null,
		},
		bone_end: {
			name: 'B_F_L2',
			x: null,
			y: null,
			z: null,
		},
		modelVersions: [],
		side_rotate: 'front-left',
	},
	{
		bone_start: {
			name: 'B_F_R1',
			x: null,
			y: null,
			z: null,
		},
		bone_end: {
			name: 'B_F_R2',
			x: null,
			y: null,
			z: null,
		},
		modelVersions: [],
		side_rotate: 'front-right',
	},

	{
		bone_start: {
			name: 'B_P_B_L1',
			x: null,
			y: null,
			z: null,
		},
		bone_end: {
			name: 'B_P_B_L2',
			x: null,
			y: null,
			z: null,
		},
		modelVersions: [],
		side_rotate: 'front-left',
	},
	{
		bone_start: {
			name: 'B_P_F_L1',
			x: null,
			y: null,
			z: null,
		},
		bone_end: {
			name: 'B_P_F_L2',
			x: null,
			y: null,
			z: null,
		},
		modelVersions: [],
		side_rotate: 'back-left',
	},
];

export const dataCornerTrimCornerPorch = {
	left: [
		{
			bone_start: {
				name: 'B_B_L1',
				x: null,
				y: null,
				z: null,
			},
			bone_end: {
				name: 'B_B_L2',
				x: null,
				y: null,
				z: null,
			},
			modelVersions: [],
			side_rotate: 'back-left',
		},
		{
			bone_start: {
				name: 'B_B_R1',
				x: null,
				y: null,
				z: null,
			},
			bone_end: {
				name: 'B_B_R2',
				x: null,
				y: null,
				z: null,
			},
			modelVersions: [],
			side_rotate: 'back-right',
		},
		{
			bone_start: {
				name: 'B_P_F_R1',
				x: null,
				y: null,
				z: null,
			},
			bone_end: {
				name: 'B_P_F_R2',
				x: null,
				y: null,
				z: null,
			},
			modelVersions: [],
			side_rotate: 'front-left',
		},
		{
			bone_start: {
				name: 'B_F_R1',
				x: null,
				y: null,
				z: null,
			},
			bone_end: {
				name: 'B_F_R2',
				x: null,
				y: null,
				z: null,
			},
			modelVersions: [],
			side_rotate: 'front-right',
		},
		{
			bone_start: {
				name: 'B_P_B_L1',
				x: null,
				y: null,
				z: null,
			},
			bone_end: {
				name: 'B_P_B_L2',
				x: null,
				y: null,
				z: null,
			},
			modelVersions: [],
			side_rotate: 'front-left',
		},
	],
	right: [
		{
			bone_start: {
				name: 'B_P_B_R1',
				x: null,
				y: null,
				z: null,
			},
			bone_end: {
				name: 'B_P_B_R2',
				x: null,
				y: null,
				z: null,
			},
			modelVersions: [],
			side_rotate: 'back-left',
		},
		{
			bone_start: {
				name: 'B_B_R1',
				x: null,
				y: null,
				z: null,
			},
			bone_end: {
				name: 'B_B_R2',
				x: null,
				y: null,
				z: null,
			},
			modelVersions: [],
			side_rotate: 'back-right',
		},
		{
			bone_start: {
				name: 'B_F_L1',
				x: null,
				y: null,
				z: null,
			},
			bone_end: {
				name: 'B_F_L2',
				x: null,
				y: null,
				z: null,
			},
			modelVersions: [],
			side_rotate: 'front-left',
		},
		{
			bone_start: {
				name: 'B_F_R1',
				x: null,
				y: null,
				z: null,
			},
			bone_end: {
				name: 'B_F_R2',
				x: null,
				y: null,
				z: null,
			},
			modelVersions: [],
			side_rotate: 'front-right',
		},
		{
			bone_start: {
				name: 'B_P_F_L1',
				x: null,
				y: null,
				z: null,
			},
			bone_end: {
				name: 'B_P_F_L2',
				x: null,
				y: null,
				z: null,
			},
			modelVersions: [],
			side_rotate: 'back-left',
		},
	],
};

export const dataCornerTrimLShapePorch = {
	left: [
		{
			bone_start: {
				name: 'B_B_L1',
				x: null,
				y: null,
				z: null,
			},
			bone_end: {
				name: 'B_B_L2',
				x: null,
				y: null,
				z: null,
			},
			modelVersions: [],
			side_rotate: 'back-left',
		},
		{
			bone_start: {
				name: 'B_B_R1',
				x: null,
				y: null,
				z: null,
			},
			bone_end: {
				name: 'B_B_R2',
				x: null,
				y: null,
				z: null,
			},
			modelVersions: [],
			side_rotate: 'back-right',
		},
		{
			bone_start: {
				name: 'B_P_F_L1',
				x: null,
				y: null,
				z: null,
			},
			bone_end: {
				name: 'B_P_F_L2',
				x: null,
				y: null,
				z: null,
			},
			modelVersions: [],
			side_rotate: 'front-left',
		},
		{
			bone_start: {
				name: 'B_P_F_R1',
				x: null,
				y: null,
				z: null,
			},
			bone_end: {
				name: 'B_P_F_R2',
				x: null,
				y: null,
				z: null,
			},
			modelVersions: [],
			side_rotate: 'front-right',
		},
		{
			bone_start: {
				name: 'B_P_B_R1',
				x: null,
				y: null,
				z: null,
			},
			bone_end: {
				name: 'B_P_B_R2',
				x: null,
				y: null,
				z: null,
			},
			modelVersions: [],
			side_rotate: 'front-right',
		},
	],
	right: [
		{
			bone_start: {
				name: 'B_B_L1',
				x: null,
				y: null,
				z: null,
			},
			bone_end: {
				name: 'B_B_L2',
				x: null,
				y: null,
				z: null,
			},
			modelVersions: [],
			side_rotate: 'back-left',
		},
		{
			bone_start: {
				name: 'B_B_R1',
				x: null,
				y: null,
				z: null,
			},
			bone_end: {
				name: 'B_B_R2',
				x: null,
				y: null,
				z: null,
			},
			modelVersions: [],
			side_rotate: 'back-right',
		},
		{
			bone_start: {
				name: 'B_P_F_L1',
				x: null,
				y: null,
				z: null,
			},
			bone_end: {
				name: 'B_P_F_L2',
				x: null,
				y: null,
				z: null,
			},
			modelVersions: [],
			side_rotate: 'front-left',
		},
		{
			bone_start: {
				name: 'B_P_F_R1',
				x: null,
				y: null,
				z: null,
			},
			bone_end: {
				name: 'B_P_F_R2',
				x: null,
				y: null,
				z: null,
			},
			modelVersions: [],
			side_rotate: 'front-right',
		},
		{
			bone_start: {
				name: 'B_P_B_L1',
				x: null,
				y: null,
				z: null,
			},
			bone_end: {
				name: 'B_P_B_L2',
				x: null,
				y: null,
				z: null,
			},
			modelVersions: [],
			side_rotate: 'front-left',
		},
	],
};

export const mappingSide = {
	front: {
		left: 'left',
		front: 'front',
		right: 'right',
	},
	side: {
		front: 'left',
		left: 'front',
		right: 'back',
	},
};

export const mappingSideRevert = {
	front: {
		left: 'left',
		front: 'front',
		right: 'right',
	},
	side: {
		left: 'front',
		front: 'left',
		back: 'right',
	},
};

export const swapComponentSideMapping = {
	front: ['front', 'porch', 'inner_front', 'diagonal_left', 'diagonal_right'],
	porch: ['porch', 'front'],
	inner_front: ['inner_front', 'inner_back', 'front'],
	left: ['left', 'left_front', 'left_back', 'front_inner_porch', 'inner_left'],
	left_front: ['left_front', 'left'],
	left_back: ['left_back', 'left'],
	inner_left: ['inner_left', 'left'],
	front_inner_porch: ['front_inner_porch', 'left'],
	back: ['back', 'inner_back'],
	inner_back: ['inner_back', 'inner_front', 'back'],
	right: ['right', 'inner_right'],
	inner_right: ['inner_right', 'right'],
	diagonal_left: ['diagonal_left', 'front', 'left'],
	diagonal_right: ['diagonal_right', 'front', 'right'],
	floor: ['floor'],
	left_inner_porch: ['left_inner_porch'],
	right_inner_porch: ['right_inner_porch'],
};

export const mainSidePlaceDoor = {
	front: 'front',
	side: 'left',
	conner: 'left',
	deluxe: 'inner_front',
	middle: 'front_inner_porch',
	none: 'front',
};

export const fullWallComponentMapping = {
	middle: {
		left: 'left_front',
	},
	front: {
		left: 'left',
		left_front: 'left',
		left_back: 'left',
		front_inner_porch: 'left',
		front: 'front',
	},
	side: {
		left: 'left',
		left_front: 'left',
		left_back: 'left',
		front_inner_porch: 'left',
		front: 'front',
		inner_left: 'left',
	},
	corner: {
		front_inner_porch: 'left',
		left_front: 'left',
		left_back: 'left',
	},
	l_shape: {
		left: 'left',
		inner_left: 'left',
		inner_front: 'inner_front',
		inner_right: 'right',
		right: 'right',
		front: 'front',
	},
	none: {
		left: 'left',
		left_front: 'left',
		left_back: 'left',
		front_inner_porch: 'left',
		front: 'front',
	},
};

export const allSidesPorch = {
	none: ['front', 'back', 'left', 'right'],
	front: ['front', 'back', 'left', 'right'],
	side: ['front', 'back', 'left', 'right'],
	middle: [
		'front',
		'back',
		'left_back',
		'left_front',
		'front_inner_porch',
		'left_inner_porch',
		'right_inner_porch',
		'right',
	],
	corner: ['front', 'back', 'left', 'right', 'inner_front', 'inner_back', 'inner_left'],
	l_shape: ['front', 'back', 'left', 'right', 'inner_front', 'inner_left', 'inner_right'],
};

export const mainSideColumn = {
	front: 'front',
	side: 'left',
	middle: 'left',
	corner: 'left',
	l_shape: 'front',
};

//l-shape left
// b_f_l4 => b_p_f_r1
// b_f_l3 => b_p_f_r2
// b_f_l1 => b_p_f_l1
// b_f_l2 => b_p_f_l2
// b_f_r1 => b_p_b_r1
// b_f_r2 => b_p_b_r2
// b_f_r3 => b_p_b_l2
// b_f_r4 => b_p_b_l1

//l-shape right
// b_f_r4 => b_p_f_l1
// b_f_r3 => b_p_f_r2
// b_f_r1 => b_p_f_r1
// b_f_r2 => b_p_f_r2
// b_f_l1 => b_p_b_l1
// b_f_l2 => b_p_b_l2
// b_f_l3 => b_p_b_r2
// b_f_l4 => b_p_b_r1
